import { useEffect, useMemo, useRef, useState } from "react";

import useCustomerDetails from "../../../data/useCustomerDetails";
import useOffers from "../../../data/useOffers";
import usePartMutations from "../../../data/usePartMutations";
import usePartsLookupMutations from "../../../data/usePartsLookupMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { FormatType, IEbayOffer } from "../../../models/EbayOffer";
import { IPart } from "../../../models/Part";
import useNotification from "../../notifications/useNotifications";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import PriceFormat from "../../../utils/priceFormat";
import { MAX_PART_PRICE } from "../../../utils/priceLimits";
import { useUserLookup } from "../../../hooks/useUserLookup";
import { DateTime } from "luxon";
import Tooltip from "../../shared/Tooltip";
import {
  ArrowPathIcon,
  CheckIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import classNames from "../../../utils/classNames";
import { ISerpApiSearchResult, PartProBlackList } from "../../../models/PartLookup";
import UnlistedIntelligencePriceControls from "./UnlistedIntelligencePriceControls";
import UnlistedIntelligenceModal from "./UnlistedIntelligenceModal";

type PriceButtonLayout = "row" | "column";

const getPriceSummary = (results: ISerpApiSearchResult[]) => {
  const prices = results
    .map((r) => r.priceExtracted)
    .filter((p): p is number => p != null && p > 0);

  if (prices.length === 0) return null;

  const round = (n: number) => Math.round(n * 100) / 100;

  return {
    min: round(Math.min(...prices)),
    max: round(Math.max(...prices)),
    avg: round(prices.reduce((sum, p) => sum + p, 0) / prices.length),
  };
};

export default function UnlistedOfferPrice({
  part,
  showButtons = true,
  hideLabel = false,
  compact = false,
  buttonLayout,
}: {
  part: IPart;
  showButtons?: boolean;
  hideLabel?: boolean;
  compact?: boolean;
  inlineIntelligence?: boolean;
  buttonLayout?: PriceButtonLayout;
}) {
  const { update } = useOffers();
  const { patchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Price");
  const { addNotification } = useNotification();
  const { lookupMutation, intelligencePricingMutation } = usePartsLookupMutations(part.id);
  const { brand } = useCustomerDetails();

  const [isEditing, setIsEditing] = useState(false);
  const [partProOpen, setPartProOpen] = useState(false);
  const [partProLoading, setPartProLoading] = useState(false);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [intelligenceAll, setIntelligenceAll] = useState<ISerpApiSearchResult[] | null>(null);
  const [intelligenceActive, setIntelligenceActive] = useState<ISerpApiSearchResult[] | null>(null);
  const [intelligenceSold, setIntelligenceSold] = useState<ISerpApiSearchResult[] | null>(null);
  const [intelligenceModalOpen, setIntelligenceModalOpen] = useState(false);
  const [draftPrice, setDraftPrice] = useState<number | null>(null);

  const intelligencePrices = useMemo(() => {
    if (intelligenceAll != null) return getPriceSummary(intelligenceAll);

    if (
      part.intelligenceMin != null &&
      part.intelligenceAverage != null &&
      part.intelligenceMax != null
    ) {
      return {
        min: part.intelligenceMin,
        avg: part.intelligenceAverage,
        max: part.intelligenceMax,
      };
    }

    return null;
  }, [intelligenceAll, part.intelligenceAverage, part.intelligenceMax, part.intelligenceMin]);

  const priceRef = useRef<HTMLInputElement | null>(null);
  const hasOffers = part.offers && part.offers.length > 0;
  const persistedPrice = hasOffers
    ? part.offers[0]?.format === FormatType.Auction
      ? part.offers[0]?.auctionStartPrice ?? null
      : part.offers[0]?.buyItNowPrice ?? null
    : part.enteredPrice ?? null;
  const roundedPersistedPrice = persistedPrice == null ? null : Math.round(persistedPrice * 100) / 100;
  const roundedDraftPrice = draftPrice == null ? null : Math.round(draftPrice * 100) / 100;
  const hasDraftChanges = roundedDraftPrice !== roundedPersistedPrice;

  const handlePriceUpdate = (price: string | number) => {
    let doublePrice = typeof price === "number" ? price : parseFloat(price);

    if (isNaN(doublePrice)) return;

    doublePrice = Math.round(doublePrice * 100) / 100;

    if (doublePrice > MAX_PART_PRICE) {
      addNotification({ primaryText: `Price cannot exceed ${PriceFormat(MAX_PART_PRICE)}`, variant: "warn" });
      return;
    }

    if (part.offers && part.offers.length > 0) {
      const updatedOffer = {
        ...part.offers[0],
      };

      if (part.offers[0].enableBestOffer && doublePrice < (part.offers[0].autoAcceptPrice || 0)) {
        addNotification({ primaryText: "Best Offer Enabled - cannot set price below auto accept price", variant: "info" });
        return;
      }

      updateOffer(updatedOffer, doublePrice);
    } else {
      updatePart(part, doublePrice);
    }
  }

  const handleDraftChange = (price: string | number) => {
    const parsedPrice = typeof price === "number" ? price : parseFloat(price);
    if (isNaN(parsedPrice)) {
      setDraftPrice(null);
      return;
    }

    setDraftPrice(Math.min(Math.round(parsedPrice * 100) / 100, MAX_PART_PRICE));
  };

  const handleDraftSave = () => {
    if (draftPrice == null) return;
    handlePriceUpdate(draftPrice);
  };

  const handleDraftReset = () => {
    setDraftPrice(roundedPersistedPrice);
  };

  const handleIntelligenceSliderChange = (price: number) => {
    setIsEditing(true);
    handleDraftChange(price);
  };

  const handleIntelligencePercentAdjust = (percentage: number) => {
    setIsEditing(true);
    const basePrice = draftPrice ?? roundedPersistedPrice ?? intelligencePrices?.avg ?? 0;
    if (basePrice <= 0) return;
    handleDraftChange(basePrice * (1 + percentage / 100));
  };

  const updatePart = (part: IPart, price: number) => {
    patchPart.mutate({
      id: part.id,
      enteredPrice: price
    }, {
      ...saveHandlers,
      onSuccess: () => {
        part.enteredPrice = price;
        saveHandlers.onSuccess();
        setIsEditing(false);
      }
    });
  }

  const updateOffer = (offer: IEbayOffer, price: number) => {
    if (offer.format === FormatType.Auction) {
      offer.auctionStartPrice = price;
    } else {
      offer.buyItNowPrice = price;
    }

    update.mutate(offer, {
      ...saveHandlers,
      onSuccess: () => {
        const existingOffer = part.offers.find((existing) => existing.id === offer.id);
        if (existingOffer) {
          updatePrice(existingOffer, price);
        }
        saveHandlers.onSuccess();
        setIsEditing(false);
      }
    });
  }

  const updatePrice = (offer: IEbayOffer, price: number) => {
    if (offer.format === FormatType.Auction) {
      offer.auctionStartPrice = price;
    } else {
      offer.buyItNowPrice = price;
    }
  }

  const handleIntelligenceLookup = () => {
    setIntelligenceLoading(true);
    intelligencePricingMutation.mutate(undefined, {
      onSuccess: (response) => {
        const results = response?.data ?? [];
        const active = results.filter((r) => !r.isSold);
        const sold = results.filter((r) => r.isSold);

        setIntelligenceActive(active);
        setIntelligenceSold(sold);
        setIntelligenceAll(results);
        const summary = getPriceSummary(results);
        part.lastIntelligenceLookup = DateTime.now().toISO();
        part.intelligenceMin = summary?.min ?? null;
        part.intelligenceAverage = summary?.avg ?? null;
        part.intelligenceMax = summary?.max ?? null;

        if (results.length === 0) {
          addNotification({ primaryText: "Wilma Intelligence found no results", variant: "info" });
        } else {
          addNotification({ primaryText: `Wilma Intelligence Price Returned`, variant: "success" });
        }
        setIntelligenceLoading(false);
      },
      onError: (error) => {
        console.error("Wilma Intelligence lookup failed:", error);
        addNotification({ primaryText: "Wilma Intelligence Lookup Failed", variant: "error" });
        setIntelligenceLoading(false);
      },
    });
  };

  const handleIntelligenceModalOpen = () => {
    setIntelligenceModalOpen(true);
    if (!intelligenceLoading) {
      handleIntelligenceLookup();
    }
  };

  const handlePartProLookup = () => {
    if (part?.partsLookupRequestId) {
      setPartProOpen(false);
      return;
    }

    setPartProLoading(true);
    lookupMutation.mutate(undefined,
      {
        onSuccess: (data) => {
          if (!data.data.requestedParts || data.data.requestedParts.length === 0) {
            addNotification({ primaryText: "No Parts Found", variant: "info", });
            setPartProLoading(false);
            return;
          }

          part.suggestedPrice = data.data.requestedParts?.find((p) => p.partCategory?.id === part.category!.id)?.priceInformation.avgPrice || 0;
          part.lastPartLookupChecked = DateTime.now().toISO();

          addNotification({ primaryText: "PartPro Lookup Successful", variant: "success", });
        },
        onError: (error) => {
          console.error("PartPro lookup failed:", error);
          addNotification({ primaryText: "PartPro Lookup Failed", variant: "error", })
        }
      }
    );

    setPartProOpen(false);
  }

  //All this just to stop the mouse wheel changing the number input value...
  useEffect(() => {
    const el = priceRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (document.activeElement === el) {
        e.preventDefault();     // THIS actually blocks value changes
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, [isEditing]);

  useEffect(() => {
    setDraftPrice(roundedPersistedPrice);
  }, [part.id, roundedPersistedPrice]);


  const isLocal = process.env.NODE_ENV === "development";

  const { findUser, isLoading: userLookupLoading } = useUserLookup();

  const pricedBy = useMemo(() => {
    if (!part.pricedBy || userLookupLoading) return null;

    const user = findUser(part.pricedBy);
    return user?.fullname;
  }, [findUser, part.pricedBy, userLookupLoading])

  const pricedTooltip = useMemo(() => {
    const parts: string[] = [];
    if (pricedBy) parts.push(`Priced by ${pricedBy}`);
    if (part.pricedOn) {
      const dt = DateTime.fromISO(part.pricedOn);
      if (dt.isValid) parts.push(dt.toFormat("d LLL yyyy"));
    }
    return parts.length > 0 ? parts.join(" - ") : null;
  }, [pricedBy, part.pricedOn]);

  // derive rendering state
  const label = hasOffers
    ? part.offers[0]?.format === FormatType.Auction
      ? "Auction"
      : "Buy It Now"
    : `Buy It Now ${isLocal ? "**" : ""}`;
  const inputValue = draftPrice;
  const resolvedButtonLayout = buttonLayout ?? "row";

  return (
    <>
      <ConfirmModal
        open={partProOpen}
        setOpen={setPartProOpen}
        onConfirm={handlePartProLookup}
        isLoading={partProLoading}
        title={"Activate PartPro Lookup?"}
        message={`This will start a PartPro lookup on this vehicle, this may take some time.\n
                  This will check against an exact part number match, display it for you in the table, show how many it matched on and give you an accurate price estimate for the parts.\n
                  This costs 1 token per lookup per vehicle (multiple parts from the same vehicle count as one lookup), you have ${brand.data?.data.lookupTokens ?? 0
          } tokens remaining.`}
        confirmButtonText={null}
      />

      <div
        className={classNames(
          "flex flex-col gap-y-2",
          resolvedButtonLayout === "row" ? "w-full min-w-0 items-stretch" : "",
          compact ? "py-1 px-1" : "py-6 px-1"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {!hideLabel && <p className="text-xs text-gray-500">{label}</p>}
        <div
          onWheel={(e) => {
            e.preventDefault();
          }}
          title={pricedBy ? `Priced By: ${pricedBy}` : ""}
          className={classNames(
            resolvedButtonLayout === "row"
              ? "flex w-full min-w-0 items-stretch"
              : "flex items-center"
          )}
        >
          {isEditing ? (
            <label className="flex min-h-[3.25rem] min-w-0 flex-1 flex-col justify-center rounded-lg bg-gradient-to-b from-amber-50/80 to-white px-2.5 py-1.5 text-left ring-1 ring-inset ring-amber-300 shadow-sm transition focus-within:ring-2 focus-within:ring-primary-500/30">
              <span className="flex items-center justify-between gap-1">
                <span className="text-[9px] font-semibold uppercase tracking-wide text-amber-900/60">
                  {hideLabel ? label : "Price"}
                </span>
                {pricedBy && (
                  <span
                    aria-hidden
                    className="text-[9px] font-medium text-amber-900/60"
                  >
                    {pricedBy.split(" ")[0]}
                  </span>
                )}
              </span>
              <span className="mt-0.5 flex min-w-0 w-full items-center gap-1">
                <span className="mr-1 text-base font-semibold text-amber-900">£</span>
                <input
                  ref={priceRef}
                  key={part.id}
                  value={inputValue ?? ""}
                  step={1}
                  min={0}
                  max={MAX_PART_PRICE}
                  autoFocus
                  className="no-spinner min-w-0 flex-1 border-0 bg-transparent p-0 text-base font-semibold tabular-nums text-amber-900 placeholder:text-amber-300 focus:outline-none focus:ring-0"
                  type="number"
                  inputMode="decimal"
                  onChange={(e) => handleDraftChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && hasDraftChanges) {
                      handleDraftSave();
                    }
                  }}
                />
                <span className="flex w-[3.5rem] shrink-0 items-center justify-end gap-1">
                  {hasDraftChanges ? (
                    <>
                      <Tooltip content="Save price">
                        <button
                          type="button"
                          onClick={handleDraftSave}
                          className="rounded p-1 text-emerald-700 transition hover:bg-emerald-100"
                        >
                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Reset draft">
                        <button
                          type="button"
                          onClick={handleDraftReset}
                          className="rounded p-1 text-slate-600 transition hover:bg-slate-100"
                        >
                          <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </Tooltip>
                    </>
                  ) : null}
                </span>
              </span>
            </label>
          ) : (
            <Tooltip
              content={
                pricedTooltip
                  ? `${pricedTooltip} - Click to edit`
                  : "Click to edit price"
              }
              position="top"
            >
              <button
                type="button"
                onClick={() => {
                  setDraftPrice(roundedPersistedPrice);
                  setIsEditing(true);
                }}
                className="min-h-[3.25rem] w-full min-w-[7rem] flex-1 rounded-lg bg-gradient-to-b from-amber-50/80 to-white px-2.5 py-1.5 text-left ring-1 ring-inset ring-amber-200/60 shadow-sm transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
              >
                <div className="flex items-center justify-between gap-1">
                  {hideLabel ? (
                    <span className="text-[9px] font-semibold uppercase tracking-wide text-amber-900/80">
                      {label}
                    </span>
                  ) : (
                    <span className="text-[9px] font-semibold uppercase tracking-wide text-amber-900/60">
                      Price
                    </span>
                  )}
                  {pricedBy && (
                    <span
                      aria-hidden
                      className="text-[9px] font-medium text-amber-900/60"
                    >
                      {pricedBy.split(" ")[0]}
                    </span>
                  )}
                </div>
                <div
                  className={classNames(
                    "text-base font-semibold tabular-nums leading-tight text-amber-900",
                    inputValue == null && "text-slate-400"
                  )}
                >
                  {inputValue != null ? PriceFormat(inputValue) : "-"}
                </div>
              </button>
            </Tooltip>
          )}
        </div>

        {showButtons && (
          <PriceButtonGroup
            part={part}
            onPriceSelect={handlePriceUpdate}
            partProLoading={partProLoading}
            onLockedClick={() => setPartProOpen(true)}
            onIntelligenceClick={handleIntelligenceLookup}
            intelligenceLoading={intelligenceLoading}
            intelligencePrices={intelligencePrices}
            onIntelligenceSliderChange={handleIntelligenceSliderChange}
            onIntelligencePercentAdjust={handleIntelligencePercentAdjust}
            currentDraftPrice={draftPrice}
            onSparklesClick={handleIntelligenceModalOpen}
            layout={resolvedButtonLayout}
          />
        )}
      </div>

      <UnlistedIntelligenceModal
        open={intelligenceModalOpen}
        setOpen={setIntelligenceModalOpen}
        active={intelligenceActive}
        sold={intelligenceSold}
        loading={intelligenceLoading}
      />
    </>
  );
}

function PriceButtonGroup({
  part,
  onPriceSelect,
  partProLoading,
  onLockedClick,
  onIntelligenceClick,
  intelligenceLoading,
  intelligencePrices,
  onIntelligenceSliderChange,
  onIntelligencePercentAdjust,
  currentDraftPrice,
  onSparklesClick,
  layout,
}: {
  part: IPart;
  onPriceSelect: (price: number) => void;
  partProLoading: boolean;
  onLockedClick: () => void;
  onIntelligenceClick: () => void;
  intelligenceLoading: boolean;
  intelligencePrices: { min: number; max: number; avg: number } | null;
  onIntelligenceSliderChange: (price: number) => void;
  onIntelligencePercentAdjust: (percentage: number) => void;
  currentDraftPrice: number | null;
  onSparklesClick: () => void;
  layout: PriceButtonLayout;
}) {
  const partProUnlocked =
    part.partsLookupRequestId != null || part.lastPartLookupChecked != null;
  const isPartProBlacklisted = PartProBlackList.includes(
    part.vehicle?.combinedMake?.toLowerCase() ?? ""
  );
  const partProUcid = (part.category?.ucid ?? "").trim();
  const showPartProRow = !!partProUcid && !isPartProBlacklisted;
  const partProPrice = part.suggestedPrice
    ? parseFloat(Number(part.suggestedPrice).toFixed(2))
    : null;

  const marketPrice = part.marketDataPrice ?? null;
  const lastSoldPrice = part.lastSoldPrice ?? null;

  const rows: PriceButtonOption[] = [
    ...(marketPrice != null && marketPrice > 0
      ? [
        {
          label: "Market Data Price",
          short: "Market",
          price: marketPrice,
          accent: "violet" as keyof typeof TILE_TONES,
          onClick: () => onPriceSelect(marketPrice),
        },
      ]
      : []),
    ...(showPartProRow
      ? [
        {
          label: partProUnlocked
            ? "Part Number Price (PartPro)"
            : "Activate PartPro to look up an exact part-number price",
          short: "PartPro",
          price: partProPrice,
          accent: "emerald" as keyof typeof TILE_TONES,
          locked: !partProUnlocked,
          loading: partProLoading,
          onClick: () => {
            if (!partProUnlocked) {
              onLockedClick();
              return;
            }
            onPriceSelect(partProPrice ?? 0);
          },
        },
      ]
      : []),
    ...(lastSoldPrice != null && lastSoldPrice > 0
      ? [
        {
          label: "Last Sold Price",
          short: "Last Sold",
          price: lastSoldPrice,
          accent: "sky" as keyof typeof TILE_TONES,
          onClick: () => onPriceSelect(lastSoldPrice),
        },
      ]
      : []),
  ];

  return (
    <div className="flex w-full flex-col gap-2" onClick={(e) => e.stopPropagation()}>
      {rows.length > 0 && (
        <div
          className={classNames(
            "gap-1.5",
            layout === "row"
              ? "flex w-full items-stretch"
              : "flex w-[7rem] flex-col"
          )}
        >
          {rows.map((row) => (
            <PriceButtonTile key={row.short} row={row} layout={layout} />
          ))}
        </div>
      )}

      <UnlistedIntelligencePriceControls
        intelligencePrices={intelligencePrices}
        intelligenceLoading={intelligenceLoading}
        currentDraftPrice={currentDraftPrice}
        onRunLookup={onIntelligenceClick}
        onApplyAverage={() => onPriceSelect(intelligencePrices?.avg ?? 0)}
        onAdjustPercent={onIntelligencePercentAdjust}
        onSliderChange={onIntelligenceSliderChange}
        onOpenDetails={onSparklesClick}
      />
    </div>
  );
}

type PriceButtonOption = {
  label: string;
  short: string;
  price: number | null;
  accent: keyof typeof TILE_TONES;
  locked?: boolean;
  loading?: boolean;
  showWhenNoPrice?: boolean;
  onClick: () => void;
};

const TILE_TONES: Record<string, string> = {
  violet: "from-violet-50/80 to-white ring-violet-200/50 text-violet-900",
  emerald: "from-emerald-50/80 to-white ring-emerald-200/50 text-emerald-900",
  sky: "from-sky-50/80 to-white ring-sky-200/50 text-sky-900",
  amber: "from-amber-50/80 to-white ring-amber-200/50 text-amber-900",
};

function PriceButtonTile({
  row,
  layout,
}: {
  row: PriceButtonOption;
  layout: PriceButtonLayout;
}) {
  const tone = TILE_TONES[row.accent];
  const hasPrice = row.price != null && row.price > 0;
  const clickable = row.locked || hasPrice || !!row.showWhenNoPrice;

  return (
    <Tooltip
      content={row.label}
      className={layout === "row" ? "min-w-0 flex-1" : "w-full"}
    >
      <button
        type="button"
        onClick={row.onClick}
        disabled={!clickable || row.loading}
        title={
          row.locked
            ? "Click to unlock"
            : hasPrice
              ? `Click to apply ${PriceFormat(row.price!)}`
              : "No data available"
        }
        className={classNames(
          "min-h-[2.75rem] w-full rounded-lg bg-gradient-to-b px-2 py-1.5 text-left ring-1 ring-inset shadow-sm transition",
          tone,
          clickable
            ? "cursor-pointer hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            : "cursor-default opacity-60",
          row.loading && "animate-pulse"
        )}
      >
        <div className="text-[9px] font-semibold uppercase tracking-wide opacity-80">
          {row.short}
        </div>
        <div className="flex items-center gap-1 text-sm font-semibold tabular-nums leading-tight">
          {row.locked ? (
            <>
              <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="text-xs font-semibold">Unlock</span>
            </>
          ) : hasPrice ? (
            PriceFormat(row.price!)
          ) : row.showWhenNoPrice ? (
            <span className="text-xs font-semibold">Run</span>
          ) : (
            "-"
          )}
        </div>
      </button>
    </Tooltip>
  );
}